import { statusMeta, priorityMeta, trackMeta } from "@/lib/constants";
import { computeWbsIds } from "@/lib/wbsIds";

const isDone = (t) => t.health === "blue" || t.status === "done" || (t.percent_complete || 0) >= 100;
const effPct = (t) => (t.percent_complete != null ? Math.max(0, Math.min(100, t.percent_complete)) : (isDone(t) ? 100 : 0));

const durationOf = (t) => {
  if (t.is_milestone) return "0d";
  if (t.start_date && t.end_date) {
    const d = Math.round((new Date(t.end_date) - new Date(t.start_date)) / 864e5) + 1;
    return d > 0 ? `${d}d` : "—";
  }
  if (t.effort_days != null) return `${t.effort_days}d`;
  return "—";
};

export function buildWbsRows(phases, tasks) {
  const msIds = {};
  let c = 0;
  tasks.filter((t) => t.is_milestone).forEach((t) => { msIds[t.id] = `M${++c}`; });
  const childrenOf = (id) => tasks.filter((t) => t.parent_id === id).sort((a, b) => (a.order || 0) - (b.order || 0));
  const rowPct = (t) => {
    if (isDone(t)) return 100;
    const kids = childrenOf(t.id);
    if (kids.length) return Math.round(kids.reduce((a, k) => a + effPct(k), 0) / kids.length);
    return effPct(t);
  };
  const rows = [];
  phases.forEach((g, phaseIdx) => {
    const all = tasks.filter((t) => t.phase === g);
    if (!all.length) return;
    const top = all.filter((t) => !t.parent_id || !all.some((p) => p.id === t.parent_id)).sort((a, b) => (a.order || 0) - (b.order || 0));
    const walk = (t, num, depth) => {
      const kids = childrenOf(t.id);
      rows.push({
        phase: g,
        id: t.is_milestone ? msIds[t.id] : `${phaseIdx + 1}.${num}`,
        depth,
        title: t.title || "",
        type: t.is_milestone ? "Milestone" : "Task",
        start: t.start_date || "",
        end: t.end_date || "",
        duration: durationOf(t),
        pct: rowPct(t),
        status: statusMeta(t.status).label,
        priority: priorityMeta(t.priority).label,
        track: trackMeta(t.track_status).label,
        assignee: t.assignee || "",
        isMilestone: !!t.is_milestone,
      });
      kids.forEach((k, i) => walk(k, `${num}.${i + 1}`, depth + 1));
    };
    top.forEach((t, i) => walk(t, i + 1, 0));
  });
  return rows;
}

export function exportWbsCsv(projectName, phases, tasks) {
  const rows = buildWbsRows(phases, tasks);
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ["Phase", "WBS ID", "Title", "Type", "Start", "End", "Duration", "% Complete", "Status", "Priority", "Track Status", "Assignee"];
  const lines = [header.join(",")].concat(
    rows.map((r) => [r.phase, r.id, `${"  ".repeat(r.depth)}${r.title}`, r.type, r.start, r.end, r.duration, r.pct, r.status, r.priority, r.track, r.assignee].map(esc).join(","))
  );
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${(projectName || "project").replace(/[^\w-]+/g, "_")}_WBS.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

const escHtml = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function printWbs(projectName, phases, tasks, phaseColors = {}) {
  const rows = buildWbsRows(phases, tasks);
  const grouped = {};
  rows.forEach((r) => (grouped[r.phase] = grouped[r.phase] || []).push(r));
  const sections = Object.entries(grouped).map(([phase, list]) => {
    const color = phaseColors[phase] || "#0044FF";
    const body = list.map((r) => `
      <tr class="${r.isMilestone ? "ms" : ""}">
        <td class="num">${escHtml(r.id)}</td>
        <td style="padding-left:${8 + r.depth * 16}px">${r.isMilestone ? "◆ " : ""}${escHtml(r.title)}</td>
        <td>${escHtml(r.start) || "—"}</td>
        <td>${escHtml(r.end) || "—"}</td>
        <td>${escHtml(r.duration)}</td>
        <td>${r.pct}%</td>
        <td>${escHtml(r.status)}</td>
        <td>${escHtml(r.priority)}</td>
        <td>${escHtml(r.assignee) || "—"}</td>
      </tr>`).join("");
    return `
      <h2 style="border-left:4px solid ${color}">${escHtml(phase)} <span class="count">${list.length}</span></h2>
      <table>
        <thead><tr><th>ID</th><th>Task / Milestone</th><th>Start</th><th>End</th><th>Dur</th><th>%</th><th>Status</th><th>Priority</th><th>Assignee</th></tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  }).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(projectName)} — WBS</title><style>
    body{font-family:Arial,Helvetica,sans-serif;margin:28px;color:#0f172a}
    h1{font-size:20px;margin:0 0 2px}
    .meta{color:#64748b;font-size:11px;margin-bottom:16px}
    h2{font-size:13px;margin:18px 0 6px;padding-left:8px}
    .count{color:#94a3b8;font-weight:normal;font-size:11px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th{background:#f1f5f9;text-align:left;padding:5px 8px;border:1px solid #e2e8f0;text-transform:uppercase;font-size:9px;letter-spacing:.04em;color:#475569}
    td{padding:4px 8px;border:1px solid #e2e8f0;vertical-align:top}
    tr.ms td{font-weight:bold;background:#f8fafc}
    .num{font-family:monospace;color:#64748b;white-space:nowrap}
    @media print{ h2{page-break-after:avoid} tr{page-break-inside:avoid} }
  </style></head><body>
    <h1>${escHtml(projectName)} — Work Breakdown Structure</h1>
    <p class="meta">Generated ${new Date().toLocaleString()} · ${rows.length} items · Project Management Orchestrator</p>
    ${sections}
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 350);
}

const PRINT_HEALTH = { green: "#10B981", yellow: "#F59E0B", red: "#EF4444" };
const printTaskHealth = (t) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const done = t.status === "done" || (t.percent_complete || 0) >= 100;
  if (done) return "green";
  if (t.track_status === "jeopardy" || t.status === "blocked" || (t.end_date && t.end_date < todayStr)) return "red";
  if (t.track_status === "at_risk") return "yellow";
  if (t.start_date && t.end_date) {
    const s = new Date(t.start_date), e = new Date(t.end_date), n = new Date();
    const total = e - s;
    if (total > 0 && n > s) {
      const expected = Math.min(100, ((n - s) / total) * 100);
      if ((t.percent_complete || 0) + 20 < expected) return "yellow";
    }
  }
  return "green";
};

export function printFlow(projectName, phases, tasks, phaseColors = {}, phaseLabels = {}) {
  const wbsIds = computeWbsIds(tasks, phases);
  const byId = Object.fromEntries(tasks.map((t) => [t.id, t]));
  const dOf = (t) => t.end_date || t.start_date || "9999";
  const fmt = (s) => (s ? new Date(s + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
  const cards = phases.map((ph, pi) => {
    const items = tasks.filter((t) => t.phase === ph);
    if (!items.length) return "";
    const ioInputs = items.filter((t) => !t.is_summary && t.flow_role === "input").sort((a, b) => (dOf(a) < dOf(b) ? -1 : 1));
    const ioOutputs = items.filter((t) => !t.is_summary && t.flow_role === "output").sort((a, b) => (dOf(a) < dOf(b) ? -1 : 1));
    const rest = items.filter((t) => t.is_summary || !t.flow_role);
    const milestones = rest.filter((t) => t.is_milestone).sort((a, b) => (dOf(a) < dOf(b) ? -1 : 1));
    const work = rest.filter((t) => !t.is_milestone && !t.is_summary).sort((a, b) => (dOf(a) < dOf(b) ? -1 : 1));
    const summary = items.find((t) => t.is_summary);
    const output = ioOutputs[ioOutputs.length - 1] || milestones[milestones.length - 1] || null;
    const extraOutputs = ioOutputs.length > 1 ? ioOutputs.slice(0, -1) : [];
    const mids = milestones.filter((m) => m.id !== output?.id);
    const inputs = [];
    items.forEach((t) => (t.dependencies || []).forEach((d) => {
      const pid = typeof d === "string" ? d : d.predecessor_id;
      const p = byId[pid];
      if (p && p.phase !== ph && !inputs.some((x) => x.id === p.id) && !ioInputs.some((x) => x.id === p.id)) inputs.push(p);
    }));
    const color = phaseColors[ph] || "#1c6cb5";
    const chip = (p) => `<div class="chip">${p.is_milestone ? "◆ " : ""}<b>${escHtml(wbsIds[p.id] || "")}</b> ${escHtml(p.title)}</div>`;
    const explicitHtml = ioInputs.map(chip).join("");
    const depHtml = inputs.map(chip).join("");
    const inputHtml = (explicitHtml + depHtml)
      || (pi === 0 ? '<div class="chip">Project kickoff — no upstream input required</div>'
      : '<div class="warn">⚠ No input — nothing feeds this phase</div>');
    const taskRows = work.map((t) => {
      const hc = PRINT_HEALTH[printTaskHealth(t)];
      return `<div class="task" style="background:${hc}1c;border-color:${hc}"><span class="hdot" style="background:${hc}"></span><b>${escHtml(wbsIds[t.id] || "")}</b> ${escHtml(t.title)}<span class="dt">${t.percent_complete || 0}% · ${fmt(t.end_date)}</span></div>`;
    }).join("") || '<div class="dt">No tasks yet</div>';
    const midHtml = mids.length ? `<div class="mscol">${mids.map((m) => `<div class="ms">◆ <b>${escHtml(wbsIds[m.id] || "")}</b> ${escHtml(m.title)}</div>`).join("")}</div>` : "";
    const outHtml = output
      ? `<div class="out"><span class="lbl">OUTPUT: ${output.is_milestone ? "MILESTONE" : "TASK"}</span><div class="otitle">${output.is_milestone ? "◆ " : ""}${escHtml(output.title)}</div>${extraOutputs.map((o) => `<div class="oextra">${o.is_milestone ? "◆ " : "• "}${escHtml(o.title)}</div>`).join("")}<span class="promise">(what was promised)</span></div>
         <div class="deliv">Delivery: ${fmt(output.end_date || output.start_date)}</div>`
      : '<div class="warn out-warn">⚠ No output milestone — what does this phase promise?</div>';
    return `
      <div class="card" style="border-color:${color}">
        <h2><span class="dot" style="background:${color}"></span>Phase ${escHtml(String(phaseLabels[ph] || pi + 1))} · ${escHtml(ph)}</h2>
        <div class="row">
          <div class="input">${inputHtml}</div>
          <div class="mid">
            <div class="sum">Summary (goal): ${escHtml(summary?.title || `Deliver ${ph}`)}</div>
            <div class="midrow"><div class="tcol">${taskRows}</div>${midHtml}</div>
          </div>
          <div class="arrow">➤➤</div>
          <div class="outcol">${outHtml}</div>
        </div>
      </div>`;
  }).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(projectName)} — Phase Flow</title><style>
    body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#0f172a}
    h1{font-size:19px;margin:0 0 2px}
    .meta{color:#64748b;font-size:11px;margin-bottom:14px}
    .card{border:2px solid #1c6cb5;border-radius:16px;padding:12px 14px;margin-bottom:14px;page-break-inside:avoid}
    h2{font-size:13px;margin:0 0 8px;display:flex;align-items:center;gap:6px}
    .dot{width:10px;height:10px;border-radius:50%;display:inline-block}
    .row{display:flex;gap:10px;align-items:stretch}
    .input{width:170px;flex-shrink:0;background:#1c5f9e;color:#fff;border-radius:6px;padding:8px;font-size:10px}
    .input .chip{background:rgba(255,255,255,.15);border-radius:4px;padding:3px 5px;margin-bottom:3px}
    .warn{background:#fef3c7;border:1.5px dashed #f59e0b;color:#92400e;border-radius:6px;padding:8px;font-size:10px;font-weight:bold}
    .input .warn{background:#fef3c7}
    .mid{flex:1;min-width:0}
    .sum{background:linear-gradient(90deg,#1a73c9,#6fb7f0);color:#fff;text-align:center;font-size:10px;font-weight:bold;border-radius:4px;padding:4px;margin-bottom:5px}
    .midrow{display:flex;gap:8px}
    .tcol{flex:1}
    .task{border:1px solid #9cc6ea;color:#0f172a;border-radius:4px;padding:3px 6px;margin-bottom:3px;font-size:10px;display:flex;gap:5px;align-items:center}
    .hdot{width:7px;height:7px;border-radius:50%;flex-shrink:0;display:inline-block}
    .legend{display:flex;align-items:center;gap:14px;font-size:10px;color:#475569;margin:0 0 12px}
    .legend b{font-size:9px;text-transform:uppercase;letter-spacing:.05em}
    .legend span{display:inline-flex;align-items:center;gap:5px;font-weight:600}
    .legend i{width:9px;height:9px;border-radius:50%;display:inline-block}
    .task .dt{margin-left:auto;opacity:.7}
    .dt{font-size:9px;color:#64748b}
    .mscol{max-width:40%}
    .ms{background:#1565c0;color:#fff;border-radius:4px;padding:3px 6px;margin-bottom:3px;font-size:10px}
    .arrow{align-self:center;color:#57bb63;font-size:20px;font-weight:bold;flex-shrink:0}
    .outcol{width:170px;flex-shrink:0;display:flex;flex-direction:column;gap:5px}
    .out{background:linear-gradient(135deg,#2f7fc1,#5cb86a);color:#fff;border-radius:6px;padding:8px;font-size:10px;flex:1}
    .out .lbl{font-size:8px;font-weight:bold;letter-spacing:.05em}
    .out .otitle{font-size:11px;font-weight:bold;margin:3px 0}
    .out .promise{opacity:.75}
    .out .oextra{font-size:10px;opacity:.9;margin:2px 0}
    .out-warn{background:#fee2e2;border-color:#ef4444;color:#991b1b;flex:1}
    .deliv{border:1px solid #34d399;background:#ecfdf5;color:#065f46;font-size:10px;font-weight:bold;text-align:center;border-radius:6px;padding:5px}
    @media print{ .card{page-break-inside:avoid} body{-webkit-print-color-adjust:exact;print-color-adjust:exact} }
  </style></head><body>
    <h1>${escHtml(projectName)} — Phase Flow (Input → Tasks → Output)</h1>
    <p class="meta">Every phase consumes an input and promises an output milestone · Generated ${new Date().toLocaleString()} · Project Management Orchestrator</p>
    <div class="legend"><b>Task colors:</b>
      <span><i style="background:${PRINT_HEALTH.green}"></i>On track / done</span>
      <span><i style="background:${PRINT_HEALTH.yellow}"></i>Behind / at risk</span>
      <span><i style="background:${PRINT_HEALTH.red}"></i>Overdue / jeopardy</span>
    </div>
    ${cards}
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 350);
}

export function printPersonCard(card, phaseFilter = "") {
  const projects = (card.projects || []).map((pj) => ({
    ...pj,
    tasks: phaseFilter ? (pj.tasks || []).filter((t) => (t.phase || "No phase") === phaseFilter) : (pj.tasks || []),
  })).filter((pj) => pj.tasks.length || (!phaseFilter && (pj.actions || []).length));
  const openTasks = projects.reduce((a, p) => a + p.tasks.length, 0);
  const openActions = phaseFilter ? 0 : projects.reduce((a, p) => a + (p.actions || []).length, 0);
  const overdue = projects.reduce((a, p) => a + p.tasks.filter((t) => t.overdue).length, 0);
  const sections = projects.map((pj) => {
    const order = []; const map = {};
    pj.tasks.forEach((t) => { const ph = t.phase || "No phase"; if (!map[ph]) { map[ph] = []; order.push(ph); } map[ph].push(t); });
    const rows = order.map((ph) => `
      <tr class="ph"><td colspan="5">${escHtml(ph)}</td></tr>
      ${map[ph].map((t) => `
        <tr class="${t.is_milestone ? "ms" : ""}">
          <td class="num">${escHtml(t.wbs_id) || "—"}</td>
          <td>${t.is_milestone ? "◆ " : ""}${escHtml(t.title)}</td>
          <td>${t.percent_complete}%</td>
          <td class="${t.overdue ? "late" : ""}">${escHtml(t.end_date) || "—"}</td>
          <td class="${t.overdue ? "late" : ""}">${t.overdue ? "OVERDUE" : escHtml((t.track_status || "on_track").replace(/_/g, " "))}</td>
        </tr>`).join("")}`).join("");
    const actions = !phaseFilter && pj.actions?.length ? `
      <p class="sub">Action items (${pj.actions.length})</p>
      <ul>${pj.actions.map((a) => `<li>${a.display_id ? `<span class="num">${escHtml(a.display_id)}</span> ` : ""}${escHtml(a.title)} <span class="mut">(${escHtml(a.status)}${a.due_date ? ` · due ${escHtml(a.due_date)}` : ""})</span>${a.bucket === "late" ? ' <b class="late">LATE</b>' : ""}</li>`).join("")}</ul>` : "";
    return `<h2>${escHtml(pj.project_name)}</h2>
      ${pj.tasks.length ? `<table><thead><tr><th>WBS</th><th>Task / Milestone</th><th>%</th><th>Due</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>` : ""}
      ${actions}`;
  }).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(card.person)} — Resource Card</title><style>
    body{font-family:Arial,Helvetica,sans-serif;margin:28px;color:#0f172a}
    h1{font-size:20px;margin:0 0 2px}
    .meta{color:#64748b;font-size:11px;margin-bottom:4px}
    .chips{margin:6px 0 14px;font-size:12px;color:#334155}
    .chips b{color:#0044FF}
    .filter{display:inline-block;background:#EFF6FF;border:1px solid #BFDBFE;color:#1D4ED8;border-radius:999px;padding:2px 10px;font-size:11px;font-weight:700;margin-bottom:12px}
    h2{font-size:13px;margin:16px 0 6px;padding-left:8px;border-left:4px solid #0044FF;text-transform:uppercase;letter-spacing:.04em}
    table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:6px}
    th{background:#f1f5f9;text-align:left;padding:5px 8px;border:1px solid #e2e8f0;text-transform:uppercase;font-size:9px;letter-spacing:.04em;color:#475569}
    td{padding:4px 8px;border:1px solid #e2e8f0;vertical-align:top}
    tr.ph td{background:#f8fafc;font-weight:700;font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:#0044FF}
    tr.ms td{font-weight:bold}
    .num{font-family:monospace;color:#0044FF;font-weight:700;white-space:nowrap}
    .late{color:#DC2626;font-weight:700}
    .mut{color:#64748b}
    .sub{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:#475569;margin:8px 0 3px}
    ul{margin:0 0 8px;padding-left:18px;font-size:12px}
    .note{background:#EFF6FF;border-left:4px solid #0044FF;border-radius:6px;padding:8px 12px;font-size:12px;color:#1e3a8a;margin:0 0 12px}
    @media print{ h2{page-break-after:avoid} tr{page-break-inside:avoid} }
  </style></head><body>
    <h1>${escHtml(card.person)} — Resource Card</h1>
    <p class="meta">Generated ${new Date().toLocaleString()} · Project Management Orchestrator</p>
    <p class="chips"><b>${openTasks}</b> open task${openTasks === 1 ? "" : "s"}${openActions ? ` · <b>${openActions}</b> action item${openActions === 1 ? "" : "s"}` : ""}${overdue ? ` · <b class="late">${overdue} overdue</b>` : ""}</p>
    ${phaseFilter ? `<span class="filter">Filtered to phase: ${escHtml(phaseFilter)}</span>` : ""}
    ${card.note ? `<div class="note"><b>Note:</b> ${escHtml(card.note)}</div>` : ""}
    ${sections || "<p>No open items.</p>"}
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 350);
}
